import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import DayPicker from "react-day-picker";
import { API, Helpers, Router } from "../Helpers";
import NumberInput from "../NumberInput";
import _ from "lodash";

export default function BookingWidget({
  editMode = false,
  defaultValues,
  onAddedToCart: onAddedToCartPropCallback = (f) => f,
  onPriceChange = (f) => f,
  beforeAddedToCart = (f) => f,
  redirect,
  ...props
}) {
  const {
    env,
    csrf_token,
    tour,
    product_items: productItems,
    pickup_options: pickupLocationOptions,
    active_site: activeSite,
    routes,
    RenderOnAddedToCart,
    pickupLocationLabel = "Where can we pick you up?",
  } = props;

  const [pickupDate, setPickupDate] = useState(props.pickup_date);
  const [selectedProductItem, setProductItem] = useState();
  const [selectedPickupLocation, setPickupLocation] = useState();
  const [pickupTimeOptions, setPickupTimeOptions] = useState([]);
  const [selectedPickupTime, setPickupTime] = useState();
  const [participants, setParticipants] = useState(getDefaultParticipants());

  const [totalPrice, setTotalPrice] = useState(0);
  const [cartErrorMessage, setCartErrorMessage] = useState();
  const [isAddingToCart, setAddingToCart] = useState(false);
  const [itemAdded, setItemAdded] = useState(false);
  const [cart, setCart] = useState(props.cart);
  const [tierAvailable, setTierAvailable] = useState(false);

  const [step, setStep] = useState(!editMode || !defaultValues ? 1 : 2);
  const participantsDropdownRef = useRef();

  const primaryColor = useMemo(() => {
    const color = activeSite.settings.data.find(
      (item) => item.key === "primary_color"
    );
    return color.value;
  }, [activeSite]);

  useEffect(() => {
    if (itemAdded)
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
  }, [itemAdded]);

  useEffect(() => {
    if (!editMode || !defaultValues) return;
    const { pickupLocation, pickupTime, participants, price } = defaultValues;
    if (!pickupTime) {
      return;
    }

    updatePickupDate(new Date(pickupTime.startTime), { disabled: false });
    updatePickupLocation(pickupLocation);
    updatePickupTime({
      label:
        Helpers.toTimeString(pickupTime.startTime) +
        ` (ends ${Helpers.toTimeString(pickupTime.endTime)})`,
      value: pickupTime,
    });
    setParticipants(participants);
    changePrice(price);
  }, [editMode]);

  function changePrice(price) {
    setTotalPrice(price);
    onPriceChange(price);
  }

  const isDisabledDate = (day) => {
    let productItem = _.find(
      productItems.items,
      (productItem) => productItem.date == Helpers.toDateString(day)
    );

    if (_.isEmpty(productItem)) return true;

    let disabled = true;

    _.forEach(productItem.timesAvailable, (timeAvailable) => {
      _.forEach(Object.keys(timeAvailable.tiersAvailable), (key) => {
        disabled = timeAvailable.tiersAvailable[key] >= 1 ? false : disabled;
      });
    });

    // Check Booking Block Out Time tags
    const tags = tour.tags.data;
    const generalSettingsBlockOutTime = _.find(
      activeSite.settings.data,
      (setting) => setting.key == "booking_block_out_time"
    );
    const bookingBlockOutTime = _.find(
      tags,
      (tag) => tag.tag_group_name === "Booking Block Out Times"
    );
    let blockOutTime = 24;

    if (!_.isEmpty(generalSettingsBlockOutTime))
      blockOutTime = generalSettingsBlockOutTime.value;

    if (!_.isEmpty(bookingBlockOutTime))
      blockOutTime = bookingBlockOutTime.name;

    let myDate = new Date();

    myDate.setHours(myDate.getHours() + (parseInt(blockOutTime) || 24));

    if (Helpers.toDateString(day) <= Helpers.toDateString(myDate))
      disabled = true;

    return disabled;
  };

  const updatePickupLocation = (location) => {
    setPickupLocation(location);
    setPickupTime(null);
    setParticipants(getDefaultParticipants());
    changePrice(0);

    setStep(3);
  };

  const updatePickupTime = (time) => {
    setPickupTime(time);
    setParticipants(getDefaultParticipants());
    changePrice(0);
    setStep(4);
  };

  const updatePickupDate = (pickupDate, modifiers) => {
    if (modifiers.disabled) return;

    const productItem = _.find(
      productItems.items,
      (pItem) => pItem.date == Helpers.toDateString(pickupDate)
    );
    setProductItem(productItem);
    setPickupDate(pickupDate);
    setPickupTimeOptions(
      productItem.timesAvailable.map((timeAvailable) => ({
        label:
          Helpers.toTimeString(timeAvailable.startTime) +
          ` (ends ${Helpers.toTimeString(timeAvailable.endTime)})`,
        value: timeAvailable,
      }))
    );

    //clear all other fields
    setPickupTime(null);
    setPickupLocation(null);
    changePrice(0);
    setParticipants(getDefaultParticipants());
    setCartErrorMessage("");

    setStep(2);
  };

  function getDefaultParticipants() {
    const participants = {};

    _.forEach(productItems.priceTiers, (priceTier) => {
      participants[priceTier.key] = 0;
    });

    return participants;
  }

  function addParticipant(priceTier) {
    const newParticipants = { ...participants };

    newParticipants[priceTier.key] = !_.isNumber(newParticipants[priceTier.key])
      ? 0
      : newParticipants[priceTier.key] + 1;

    setParticipants(newParticipants);
    updatePrice(newParticipants);
    isTiersAvailable(newParticipants);
  }

  function removeParticipant(priceTier) {
    const newParticipants = { ...participants };
    newParticipants[priceTier.key] =
      newParticipants[priceTier.key] > 0
        ? newParticipants[priceTier.key] - 1
        : 0;

    setParticipants(newParticipants);
    updatePrice(newParticipants);
    isTiersAvailable(newParticipants);
  }

  function updatePrice(participants) {
    let newTotalPrice = 0;
    _.forEach(Object.keys(participants), (key) => {
      let participantsNo = participants[key];
      let itemPrice = selectedPickupTime.value.price[key];
      let pickupPrice = selectedPickupLocation.pickup_details[0].price[key];

      newTotalPrice += participantsNo * (itemPrice + pickupPrice);
    });

    changePrice(newTotalPrice);
  }

  function isTiersAvailable(participants) {
    const productItem = _.find(
      selectedProductItem.timesAvailable,
      (pItem) => pItem.activityId == selectedPickupTime.value.activityId
    );
    let isNotAvailable = false;
    let participantsSum = getPaticipantsSum(participants);
    let tiersAvailable = 0;

    if (productItem.minimumQuantity > participantsSum) {
      setTierAvailable(false);
      setCartErrorMessage(
        `A minimum of ${productItem.minimumQuantity} guests is required.`
      );
      return;
    }

    _.forEach(Object.keys(productItem.tiersAvailable), (key) => {
      tiersAvailable = productItem.tiersAvailable[key];
      isNotAvailable = participantsSum > tiersAvailable ? true : false;
    });

    if (isNotAvailable) {
      setTierAvailable(false);
      setCartErrorMessage(
        `The tour on ${Helpers.toFormattedDateString(
          productItem.startTime
        )} is not available for ${participantsSum} persons. Please make an alternate selection.`
      );
    } else {
      setTierAvailable(true);
      setCartErrorMessage(false);
    }
  }

  function getPaticipantsSum(participants) {
    if (!participants) return 0;
    const participantsArray = [];
    _.forEach(Object.keys(participants), (key) => {
      participantsArray.push(participants[key]);
    });

    return participantsArray.reduce((a, b) => a + b, 0);
  }

  function measureAddToCart(product) {
    let products = [];

    _.forEach(participants, (value, key) => {
      products.push({
        name: product.name,
        id: product.resmark_id,
        price: selectedPickupTime.value.price[key],
        category: product.categories_string,
        variant: key,
        quantity: value,
      });
    });

    //Google Analytics 4
    dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        items: products,
      },
    });

    //Universal Analytics (old)
    dataLayer.push({
      event: "addToCart",
      ecommerce: {
        currencyCode: "USD",
        add: {
          products: products,
        },
      },
    });
  }

  function saveItemToLocalCart(cartItem) {
    const now = new Date();
    const newItem = { item: cartItem, tour: tour };
    let cart;

    if (localStorage.getItem("cart") !== null) {
      cart = JSON.parse(localStorage.getItem("cart"));
      if (cart.expiration < now.getTime()) {
        cart.items = [{ item: cartItem, tour: tour }];
      } else {
        const itemIndex = cart.items.findIndex(
          ({ tour: localTour }) => localTour.id == tour.id
        );
        if (itemIndex >= 0) cart.items[itemIndex].item = cartItem;
        else cart.items.push(newItem);
      }
    } else {
      cart = {
        items: [newItem],
      };
    }
    cart.expiration = now.getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("cart", JSON.stringify(cart));
  }

  async function addToCart(event) {
    event.preventDefault();
    setCartErrorMessage(false);

    const form = event.target;
    const formData = new FormData(form);
    const router = Router(routes);

    let payload = Helpers.serializeFormData(formData);

    payload.participants = participants;

    const onResponse = {
      success: (response) => {
        setCart(response);
        measureAddToCart(tour);
        onAddToCart(cart);
        saveItemToLocalCart(response.items.at(-1));

        if (redirect) {
          window.location.replace("/cart");
        } else {
          onAddedToCartPropCallback(response, tour);
        }
      },
      error: (error) => {
        setCartErrorMessage("Oops! Something went wrong.");
      },
      complete: () => {
        setAddingToCart(false);
      },
    };

    setAddingToCart(true);

    try {
      await beforeAddedToCart();
    } catch (error) {
      setCartErrorMessage("Something went wrong. Close and try again");
      return;
    }

    API.post(
      onResponse,
      payload,
      router.url("addItemToCart", { domain: activeSite.domain, cart: cart.id })
    );
  }

  function onAddToCart(data) {
    pubsub.publish("UPDATE_CART", [data]);
    setItemAdded(true);
  }

  function getParticipantsLabel(participants) {
    const labels = [];
    Object.values(productItems.priceTiers).forEach((priceTier) => {
      const value = participants[priceTier.key];
      if (!value || value == 0) return;

      labels.push(`(${value}) ${priceTier.label}`);
    });

    if (!_.isEmpty(labels)) {
      return labels.join(", ");
    }

    const tags = JSON.parse(tour.resmark_tags);
    const perUnit = tags.find((tag) => tag.name == "per unit");
    const label = perUnit ? "How many units?" : "How many persons?";

    if (getPaticipantsSum(participants) > 0) {
      return (
        `${getPaticipantsSum(participants)} ` +
        (perUnit ? "unit(s)" : "person(s)")
      );
    } else {
      return label;
    }
  }

  const controlStyle = (provided = {}) => ({
    ...provided,
    outline: `2px solid ${primaryColor}`,
  });

  const optionStyle = (styles, { isDisabled, isFocused, isSelected }) => ({
    ...styles,
    backgroundColor:
      (isSelected && primaryColor) || (isFocused && "#f5f5f5") || "transparent",
    ":active": {
      ...styles[":active"],
      backgroundColor: !isDisabled && isFocused ? primaryColor : "transparent",
    },
  });

  const css = `
        .DayPicker-Day:not(DayPicker-Day--selected) {
            color: ${primaryColor}
        }
    `;

  if (RenderOnAddedToCart && itemAdded) {
    return RenderOnAddedToCart();
  }

  return (
    <div className="col px-4">
      <style>{css}</style>
      {editMode && <h5 className="body-font font-weight-bold my-3">Edit</h5>}
      <label className="text-muted m-0">When would you like to go?</label>
      <DayPicker
        disabledDays={(day) => isDisabledDate(day)}
        onDayClick={updatePickupDate}
        selectedDays={pickupDate}
        modifiersStyles={{
          today: { fontWeight: "normal" },
          selected: { backgroundColor: primaryColor, color: "white" },
        }}
      />

      {step >= 2 && (
        <div className="mb-3">
          <span className="align-self-center text-muted small font-italic">
            If you do not see your resort listed, please reach out to us
            directly via phone so we may assist you.
          </span>
          <br />
          <br />
          <Select
            menuColor="red"
            options={pickupLocationOptions}
            onChange={updatePickupLocation}
            value={selectedPickupLocation || ""}
            placeholder={pickupLocationLabel}
            styles={{
              control: (provided) =>
                step === 2
                  ? controlStyle(provided)
                  : {
                      ...provided,
                      borderColor: "lightgray",
                      boxShadow: "none",
                    },
              option: optionStyle,
            }}
          />
        </div>
      )}

      {step >= 3 && (
        <div className="mb-3">
          <Select
            isDisabled={pickupTimeOptions.length === 0}
            options={pickupTimeOptions}
            onChange={updatePickupTime}
            value={selectedPickupTime}
            placeholder="What time works best for you?"
            styles={{
              valueContainer: (provided) => ({
                ...provided,
                whiteSpace: "nowrap",
                overflow: "hidden",
              }),
              control: (provided) =>
                step === 3
                  ? controlStyle(provided)
                  : {
                      ...provided,
                      borderColor: "lightgray",
                      boxShadow: "none",
                    },
              option: optionStyle,
            }}
          />
        </div>
      )}

      {step >= 4 && (
        <div className="dropdown mb-3">
          <button
            className={`btn border dropdown-toggle text-left ${getPaticipantsSum(
              participants
            ) == 0 && "text-muted"}`}
            style={
              step === 4
                ? controlStyle({
                    fontSize: "14px",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                  })
                : null
            }
            type="button"
            data-toggle="dropdown"
            aria-expanded="false"
          >
            {getParticipantsLabel(participants)}
          </button>

          <div
            ref={participantsDropdownRef}
            className="dropdown-menu py-3 px-4"
          >
            <form>
              {Object.values(productItems.priceTiers).map((priceTier, key) => {
                return (
                  <div className="mb-3" key={key}>
                    <NumberInput
                      label={priceTier.label}
                      ageRange={priceTier.ageRange}
                      disabled={!selectedPickupTime || !selectedPickupLocation}
                      key={key}
                      value={participants[priceTier.key] || 0}
                      onIncrement={() => addParticipant(priceTier)}
                      onDecrement={() => removeParticipant(priceTier)}
                    />
                  </div>
                );
              })}

              {!tierAvailable && (
                <p className="text-danger mb-2 small font-italic">
                  {cartErrorMessage}
                </p>
              )}

              <a
                className={`btn btn-primary btn-rounded w-100 small py-2 ${!tierAvailable &&
                  "disabled"}`}
                onClick={() =>
                  participantsDropdownRef.current.classList.remove("show")
                }
                disabled={!tierAvailable}
              >
                Done
              </a>
            </form>
          </div>
        </div>
      )}

      <div className="my-4">
        <p className="text-uppercase font-weight-bold m-0">SubTotal</p>
        <h2 className="primary-text body-font font-weight-bold mb-0 mr-1">
          {env.CURRENCY + " " + Helpers.formatCurrency(totalPrice)}
        </h2>
        <span className="align-self-center text-muted small">
          Price varies based on pickup location
        </span>
      </div>

      <form onSubmit={addToCart}>
        <input type="hidden" name="_token" value={csrf_token} readOnly={true} />
        <input type="hidden" name="cartId" value={cart.id} readOnly={true} />
        <input
          type="hidden"
          name="itemId"
          value={
            !_.isEmpty(selectedPickupTime)
              ? selectedPickupTime.value.activityId
              : ""
          }
          readOnly={true}
        />
        <input
          type="hidden"
          name="pickupDetailId"
          value={
            !_.isEmpty(selectedPickupLocation)
              ? selectedPickupLocation.pickup_details[0].id
              : ""
          }
          readOnly={true}
        />
        <input
          type="hidden"
          name="locationId"
          value={
            !_.isEmpty(selectedPickupLocation) ? selectedPickupLocation.id : ""
          }
          readOnly={true}
        />

        {cartErrorMessage && (
          <p className="text-danger mb-2 small font-italic">
            {cartErrorMessage}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-rounded btn-primary py-3 px-5 mb-3"
          disabled={
            totalPrice == 0 || itemAdded || !tierAvailable ? true : false
          }
        >
          <ul className="list-inline mb-0 d-flex align-items-center">
            {isAddingToCart && (
              <li className="list-inline-item mr-3">
                <div className="spinner-border text-white" role="status"></div>
              </li>
            )}

            <li className="list-inline-item font-weight-bold">
              {itemAdded ? (
                <React.Fragment>
                  <i className="fa fa-check mr-2"></i>
                  <span>In cart</span>
                </React.Fragment>
              ) : editMode ? (
                "Save changes"
              ) : (
                "Add To Cart"
              )}
            </li>
          </ul>
        </button>
      </form>
    </div>
  );
}



// WEBPACK FOOTER //
// ./resources/js/components/BookingWidget/BookingWidget.js